import React, { memo } from "react";
import { SearchToken } from "./searchTokens";

interface Segment {
  from: number;
  to: number;
  className: string;
}

const PLAIN = "text-foreground";
const TONES = {
  include: { key: "text-primary", value: "text-foreground" },
  exclude: { key: "text-destructive/70", value: "text-destructive" },
};

function highlightSegments(raw: string, tokens: SearchToken[]): Segment[] {
  const segments: Segment[] = [];
  let at = 0;
  for (const token of tokens) {
    if (token.from > at) segments.push({ from: at, to: token.from, className: PLAIN });
    // A "-" on a filter that cannot exclude is dropped by the parser, so it reads as plain text.
    const applied = token.filter && !(token.negated && !token.filter.excludeParam);
    if (applied) {
      const tone = token.negated ? TONES.exclude : TONES.include;
      segments.push({ from: token.from, to: token.valueFrom, className: tone.key });
      segments.push({ from: token.valueFrom, to: token.to, className: tone.value });
    } else {
      segments.push({ from: token.from, to: token.to, className: PLAIN });
    }
    at = token.to;
  }
  if (at < raw.length) segments.push({ from: at, to: raw.length, className: PLAIN });
  return segments.filter((segment) => segment.to > segment.from);
}

interface SearchHighlightProps {
  raw: string;
  tokens: SearchToken[];
  /** Where the suggestion popover lines up; a zero-width marker is placed there. */
  anchorIndex: number;
  markerRef: React.Ref<HTMLSpanElement>;
}

/**
 * The colored copy of the search text drawn behind the transparent input.
 * Colour only: every character keeps the input's metrics so the caret and
 * selection line up.
 */
const SearchHighlight = memo(function SearchHighlight({
  raw,
  tokens,
  anchorIndex,
  markerRef,
}: SearchHighlightProps) {
  const marker = <span key="marker" ref={markerRef} />;
  const nodes: React.ReactNode[] = [];
  for (const { from, to, className } of highlightSegments(raw, tokens)) {
    if (from < anchorIndex && anchorIndex < to) {
      nodes.push(
        <span key={from} className={className}>
          {raw.slice(from, anchorIndex)}
        </span>,
        marker,
        <span key={anchorIndex} className={className}>
          {raw.slice(anchorIndex, to)}
        </span>
      );
      continue;
    }
    if (from === anchorIndex) nodes.push(marker);
    nodes.push(
      <span key={from} className={className}>
        {raw.slice(from, to)}
      </span>
    );
  }
  if (anchorIndex >= raw.length) nodes.push(marker);
  return <>{nodes}</>;
});

export default SearchHighlight;
